import {
  Directive,
  HostListener,
  OnDestroy,
  TemplateRef,
  effect,
  inject,
  input,
  model,
} from '@angular/core';
import { PopupRef, PopupSettings } from '@progress/kendo-angular-popup';
import { ShivePopupService } from '../services/controls/shive-popup.service';
import { ShiveRenderService } from '../services/rendering/shive-render.service';

@Directive({
  selector: '[shivePopup]',
  standalone: true,
})
export class ShivePopupDirective implements OnDestroy {
  popupSettings = model<PopupSettings>({ ...defaultPopupSettings });
  popupAnchor = input.required<HTMLElement>();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  content = input.required<Function | TemplateRef<any>>();
  role = input('hover');

  private readonly popupService = inject(ShivePopupService);
  private readonly renderer = inject(ShiveRenderService);

  private popupRef: PopupRef | null = null;

  constructor() {
    effect(() => {
      this.popupSettings.update((settings) => ({
        ...settings,
        content: this.content(),
        anchor: this.popupAnchor(),
      }));
    });
  }

  ngOnDestroy(): void {
    this.popupRef?.close();
    this.popupRef = null;
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.role() !== 'hover') return;
    this.openPopup();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.role() !== 'hover') return;
    this.closePopup();
  }

  @HostListener('document:keydown', ['$event'])
  keydown(event: KeyboardEvent): void {
    if (event.code === 'Escape') this.closePopup();
  }

  @HostListener('document:click', ['$event'])
  documentClick(event: PointerEvent): void {
    const target = event.target as Node;
    const anchor = this.popupAnchor();

    // If click is on this anchor -> toggle open
    if (anchor.contains(target)) {
      // prevent other instances from treating this as "outside click" close
      event.stopPropagation();
      this.openPopup();
      return;
    }

    // If popup is open and click is outside popup + outside anchor -> close
    if (this.popupRef) {
      const popupEl = (this.popupRef as any).popup?.instance?.contentContainer?.nativeElement as
        | HTMLElement
        | undefined;

      if (popupEl?.contains(target)) return;

      this.closePopup();
    }
  }

  private openPopup(): void {
    if (this.popupRef) {
      this.popupRef.close();
      this.popupRef = null;
    }

    this.popupRef = this.popupService.open(this.popupSettings());

    // Optional: your animation/visibility classes
    // If you still have a "dataLoaded$" concept, keep it here per instance.
    queueMicrotask(() => {
      const popupEl = (this.popupRef as any).popup?.instance?.contentContainer?.nativeElement as
        | HTMLElement
        | undefined;

      if (!popupEl) {
        return;
      }
      this.renderer.removeClass(popupEl, 'tooltip-invisible');
      this.renderer.removeClass(popupEl, 'k-opacity-0');
      this.renderer.addClass(popupEl, 'tooltip-visible');
    });
  }

  private closePopup(): void {
    if (!this.popupRef) {
      return;
    }

    const popupEl = (this.popupRef as any).popup?.instance?.contentContainer?.nativeElement as
      | HTMLElement
      | undefined;

    this.popupRef.close();
    this.popupRef = null;

    if (!popupEl) {
      return;
    }

    this.renderer.removeClass(popupEl, 'tooltip-visible');
    this.renderer.addClass(popupEl, 'tooltip-invisible');
  }
}

const defaultPopupSettings: PopupSettings = {
  anchorAlign: { horizontal: 'right', vertical: 'center' },
  popupAlign: { horizontal: 'left', vertical: 'center' },
  margin: { horizontal: 20, vertical: 0 },
  popupClass: 'tooltip context-menu arrow-left px-3 k-opacity-0',
};
